import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  TREATMENT_EFFECTIVENESS_FILTERS,
  TREATMENT_EFFECTIVENESS_KPI_DEFINITIONS,
  TREATMENT_STATUS_PIE_COLORS,
} from '@constants/Reports/treatmentEffectiveness.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useTreatmentEffectiveness } from '@hooks/reports/useTreatmentEffectiveness'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import type { ReportPeriodBucket } from '@services/api/reports/finance.api'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicCountAxis,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 48
const REPORT_PERCENTAGE_Y_AXIS = {
  domain: [0, 100] as [number, number],
  ticks: [0, 20, 40, 60, 80, 100],
  tickFormatter: (v: number) => `${v}%`,
}

function formatKpiCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function buildPercentSeries(
  rows: ReportPeriodBucket[],
): Array<{ month: string; barValue: number; topLabel: string }> {
  return rows.map((row) => ({
    month: row.label,
    barValue: row.value,
    topLabel: `${row.value}%`,
  }))
}

function buildCountSeries(
  rows: ReportPeriodBucket[],
  view: ReportsDistributionView,
): Array<{ month: string; barValue: number; topLabel: string }> {
  if (view === 'Count') {
    return rows.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: row.value.toLocaleString(),
    }))
  }
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1
  return rows.map((row) => {
    const pct = Math.round((row.value / total) * 1000) / 10
    return {
      month: row.label,
      barValue: pct,
      topLabel: `${pct}%`,
    }
  })
}

function computePercentAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
  tickFormatter: (v: number) => string
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax =
    max <= 0 ? 100 : Math.min(100, Math.ceil((max * 1.15) / 10) * 10)
  const step = axisMax <= 50 ? 10 : 20
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return {
    domain: [0, axisMax],
    ticks,
    tickFormatter: (v: number) => `${v}%`,
  }
}

function TreatmentEffectivenessReport(): React.ReactElement {
  const [diagnosedView, setDiagnosedView] =
    useState<ReportsDistributionView>('Count')
  const [pendingView, setPendingView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data, bucketLabel } = useTreatmentEffectiveness(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load treatment effectiveness data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return TREATMENT_EFFECTIVENESS_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'treatment-acceptance-rate': data.acceptanceRate.value,
      'completed-vs-diagnosed-rate': data.completedVsDiagnosedRate.value,
      'total-diagnosed': data.totalDiagnosed.value,
      'pending-treatments': data.pendingTreatments.value,
    }

    return TREATMENT_EFFECTIVENESS_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'percent'
          ? formatKpiPercent(valueById[def.id])
          : formatKpiCount(valueById[def.id]),
    }))
  }, [data, loading])

  const acceptanceData = useMemo(
    () => buildPercentSeries(data?.acceptanceRate.buckets ?? []),
    [data],
  )
  const acceptanceYAxis = useMemo(
    () => computePercentAxis(acceptanceData.map((row) => row.barValue)),
    [acceptanceData],
  )

  const completedData = useMemo(
    () => buildPercentSeries(data?.completedVsDiagnosedRate.buckets ?? []),
    [data],
  )
  const completedYAxis = useMemo(
    () => computePercentAxis(completedData.map((row) => row.barValue)),
    [completedData],
  )

  const diagnosedChartData = useMemo(
    () => buildCountSeries(data?.totalDiagnosed.buckets ?? [], diagnosedView),
    [data, diagnosedView],
  )
  const diagnosedYAxis = useMemo(() => {
    if (diagnosedView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicCountAxis(
      diagnosedChartData.map((row) => row.barValue),
    )
  }, [diagnosedView, diagnosedChartData])

  const pendingChartData = useMemo(
    () => buildCountSeries(data?.pendingTreatments.buckets ?? [], pendingView),
    [data, pendingView],
  )
  const pendingYAxis = useMemo(() => {
    if (pendingView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicCountAxis(pendingChartData.map((row) => row.barValue))
  }, [pendingView, pendingChartData])

  const pieLegend = useMemo(() => {
    const buckets = data?.statusDistribution.buckets ?? []
    const total = buckets.reduce((sum, b) => sum + b.value, 0)
    return buckets.map((bucket) => {
      const percentage =
        total > 0 ? Math.round((bucket.value / total) * 1000) / 10 : 0
      return {
        id: bucket.label.toLowerCase(),
        label: bucket.label,
        count: bucket.value,
        percentage,
        color: TREATMENT_STATUS_PIE_COLORS[bucket.label] ?? 'var(--gray-400)',
      }
    })
  }, [data])

  const pieData = useMemo(
    () =>
      pieLegend.map((slice) => ({
        name: slice.label,
        value: slice.percentage,
        color: slice.color,
      })),
    [pieLegend],
  )

  const renderPercentChart = (
    chartData: Array<{ month: string; barValue: number; topLabel: string }>,
    axis: {
      domain: [number, number]
      ticks: number[]
      tickFormatter: (v: number) => string
    },
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) => `${value}%`}
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const renderCountChart = (
    chartData: ReturnType<typeof buildCountSeries>,
    axis: ReturnType<typeof computeDynamicCountAxis>,
    view: ReportsDistributionView,
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) =>
            view === 'Count' ? value.toLocaleString() : `${value}%`
          }
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={60}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.acceptanceRate.value,
      data?.completedVsDiagnosedRate.value,
      data?.totalDiagnosed.value,
      data?.pendingTreatments.value,
    ),
  )
  const acceptanceEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.acceptanceRate?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const completedEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.completedVsDiagnosedRate?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const diagnosedEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.totalDiagnosed?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const pendingEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.pendingTreatments?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const statusEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.statusDistribution?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-treatment-effectiveness">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Treatment Effectiveness">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={TREATMENT_EFFECTIVENESS_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Treatment Acceptance Rate ({bucketLabel})
                </ReportSectionHeading>
              </div>
              {acceptanceEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderPercentChart(acceptanceData, acceptanceYAxis)}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Completed vs Diagnosed ({bucketLabel})
                </ReportSectionHeading>
              </div>
              {completedEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderPercentChart(completedData, completedYAxis)}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid reports-treatment-effectiveness__triple-grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Diagnosed Treatment Volume ({bucketLabel})
                </ReportSectionHeading>
                {diagnosedEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={diagnosedView}
                    onChange={(value) =>
                      setDiagnosedView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {diagnosedEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderCountChart(
                    diagnosedChartData,
                    diagnosedYAxis,
                    diagnosedView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Pending Treatments ({bucketLabel})
                </ReportSectionHeading>
                {pendingEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={pendingView}
                    onChange={(value) =>
                      setPendingView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {pendingEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderCountChart(
                    pendingChartData,
                    pendingYAxis,
                    pendingView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card reports-treatment-effectiveness__status-distribution-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Treatment status distribution
                </ReportSectionHeading>
              </div>
              {statusEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-effectiveness__pie-body">
                  <div className="reports-treatment-effectiveness__pie-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip
                          formatter={(value: number) => `${value}%`}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={0}
                          outerRadius="100%"
                          paddingAngle={0}
                          startAngle={90}
                          endAngle={-270}
                          labelLine={false}
                        >
                          {pieData.map((entry) => (
                            <Cell
                              key={entry.name}
                              fill={entry.color}
                              stroke="var(--gray-white)"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="reports-treatment-effectiveness__legend">
                    {pieLegend.map((slice) => (
                      <li
                        key={slice.id}
                        className="reports-treatment-effectiveness__legend-row"
                      >
                        <span className="reports-treatment-effectiveness__legend-label-group">
                          <span
                            className="reports-treatment-effectiveness__legend-swatch"
                            style={{ backgroundColor: slice.color }}
                          />
                          <span className="reports-treatment-effectiveness__legend-label">
                            {slice.label}
                          </span>
                        </span>
                        <span
                          className="reports-treatment-effectiveness__legend-leader"
                          aria-hidden
                        />
                        <strong className="reports-treatment-effectiveness__legend-value">
                          {slice.percentage}%
                        </strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default TreatmentEffectivenessReport
